import { useEffect, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAppVersion } from "./useAppVersion";
import { getFromLocalStorage, saveToLocalStorage } from "@utils";
import { millisecondsInSecond } from "date-fns";
import { useSnackbar } from "notistack";
import { tracker } from "@utils/openreplay";
import { Box, Button, styled, Typography } from "@mui/material";

const SnackbarContent = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  gap: "6px",
  paddingLeft: "6px",
});

// Local storage keys for version tracking
const VERSION_ALERT_SHOWN_KEY = "version_alert_shown_for";
const VERSION_RELOAD_FAILED_LOGGED_KEY = "version_reload_failed_logged_for";
const VERSION_UPDATE_PENDING_KEY = "version_update_pending_for";

// Timing constants
const SNACKBAR_DURATION = 5000; // 5 seconds
const VERSION_CHECK_INTERVAL = millisecondsInSecond * 60; // 1 minute

/**
 * Hook that automatically checks for new app versions and forces an update by redirecting
 * to the home page when a newer version is detected.
 *
 * Periodically checks `/version.json` every minute and compares it with the local version.
 * When a new version is available, shows a snackbar and automatically reloads the app.
 * Tracks reload failures and shows success notifications after successful updates.
 *
 * @example
 * ```tsx
 * function App() {
 *   useLatestVersionChecker();
 *   return <div>Your app content</div>;
 * }
 * ```
 */
export const useLatestVersionChecker = () => {
  const localAppVersion = useAppVersion();
  const { enqueueSnackbar } = useSnackbar();
  const hasLoggedVersions = useRef(false);

  const { data: latestVersion } = useQuery<string>({
    queryKey: ["latestVersion"],
    queryFn: async () => {
      // Fetch version.json from the CloudFront distribution (public bucket)
      // Using a relative URL so it works regardless of the domain
      const response = await fetch("/version.json", {
        // Add cache-busting query parameter to ensure we get the latest version
        cache: "no-cache",
        headers: {
          "Cache-Control": "no-cache",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch version.json: ${response.statusText}`);
      }

      const data = await response.json();

      return data.version;
    },
    refetchInterval: VERSION_CHECK_INTERVAL,
    retry: 1, // Only retry once to avoid blocking if file is temporarily unavailable
    retryDelay: millisecondsInSecond * 3, // Retry after 3 seconds
  });

  const handleReload = useCallback(() => {
    // Remember that we've triggered the alert for this version
    saveToLocalStorage({
      key: VERSION_ALERT_SHOWN_KEY,
      value: latestVersion,
    });

    // Store the version we're updating to, so we can show a success message after reload
    saveToLocalStorage({
      key: VERSION_UPDATE_PENDING_KEY,
      value: latestVersion,
    });

    // Track the event in OpenReplay
    tracker?.event("VersionAlertShown", {
      currentVersion: localAppVersion,
      latestVersion,
      source: "useLatestVersionChecker hook",
    });

    // Always navigate to home and force reload with cache-busting to ensure we get
    // the latest version from CloudFront
    const url = new URL(window.location.origin);

    url.pathname = "/";
    url.searchParams.set("_t", Date.now().toString());
    window.location.replace(url.toString());
  }, [localAppVersion, latestVersion]);

  useEffect(() => {
    if (!latestVersion || !localAppVersion) return;

    const lastShownVersionAlert = getFromLocalStorage<string>({
      key: VERSION_ALERT_SHOWN_KEY,
    });

    // Log versions only once
    if (!hasLoggedVersions.current) {
      console.log({
        localAppVersion,
        lastShownVersionAlert,
        latestVersion,
      });
      hasLoggedVersions.current = true;
    }

    // Check if reload didn't work: alert was shown for latestVersion but currentVersion is still lower
    if (lastShownVersionAlert === latestVersion && localAppVersion !== latestVersion) {
      // Create a unique key for this version mismatch
      const versionMismatchKey = `${localAppVersion}:${latestVersion}`;

      const lastLoggedVersionMismatch = getFromLocalStorage<string>({
        key: VERSION_RELOAD_FAILED_LOGGED_KEY,
      });

      // Only track and log if we haven't done so for this specific mismatch yet
      if (lastLoggedVersionMismatch !== versionMismatchKey) {
        // Reload didn't update the version - track this event
        tracker?.event("VersionReloadFailed", {
          currentVersion: localAppVersion,
          latestVersion,
          source: "useLatestVersionChecker hook",
        });

        console.warn("Version reload failed to update", {
          currentVersion: localAppVersion,
          expectedVersion: latestVersion,
        });

        // Remember that we've logged this specific mismatch
        saveToLocalStorage({
          key: VERSION_RELOAD_FAILED_LOGGED_KEY,
          value: versionMismatchKey,
        });
      }
    }

    // After a successful reload where the local version matches the latest version,
    // check if there was a pending update for this version and show a one-time
    // confirmation snackbar.
    const pendingUpdateVersion = getFromLocalStorage<string>({
      key: VERSION_UPDATE_PENDING_KEY,
    });

    if (
      pendingUpdateVersion &&
      pendingUpdateVersion === latestVersion &&
      localAppVersion === latestVersion
    ) {
      enqueueSnackbar("Actualización realizada con éxito.", {
        variant: "success",
        autoHideDuration: SNACKBAR_DURATION,
      });

      // Clear the pending update flag so we don't show this again
      saveToLocalStorage({
        key: VERSION_UPDATE_PENDING_KEY,
        value: "",
      });
    }

    let timeoutId: number | undefined;

    if (localAppVersion !== latestVersion) {
      // Only trigger auto-update flow if we haven't done so for this latest version yet
      if (lastShownVersionAlert !== latestVersion) {
        console.log("New version available");

        // Inform the user that the app will navigate to home and update
        enqueueSnackbar({
          variant: "info",
          autoHideDuration: SNACKBAR_DURATION,
          message: (
            <SnackbarContent>
              <Typography style={{ fontSize: "14px" }}>
                Nueva versión disponible. Actualizaremos tu aplicación.
              </Typography>
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  handleReload();
                }}
              >
                Aceptar
              </Button>
            </SnackbarContent>
          ),
        });

        // Schedule automatic reload towards "/" after the snackbar duration
        timeoutId = window.setTimeout(() => {
          handleReload();
        }, SNACKBAR_DURATION);
      }
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [localAppVersion, latestVersion, enqueueSnackbar, handleReload]);

  useEffect(() => {
    // Wait for next tick to ensure page is fully loaded after reload
    const timeoutId = setTimeout(() => {
      const url = new URL(window.location.href);

      if (url.searchParams.has("_t")) {
        url.searchParams.delete("_t");
        // Use replace to avoid adding to history
        window.history.replaceState({}, "", url.toString());
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, []);
};
